/**
 * Zentraler Application Initializer Service
 *
 * Konsolidiert alle APP_INITIALIZER in einen einzigen Service mit definierter
 * Initialisierungsreihenfolge. Ersetzt 7 parallele Initializer durch einen
 * sequentiellen, phasenbasierten Ansatz.
 *
 * Initialisierungs-Phasen:
 * 1. CONFIG: Konfiguration laden und validieren
 * 2. LOGGING: Logger-Level aus Config setzen
 * 3. MONITORING: Sentry und Matomo initialisieren
 * 4. INTEGRATION: Portal-Integration und API-Konfiguration
 * 5. AUTH: OAuth-Status prüfen
 *
 * @class AppInitializerService
 */
import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MatomoInitializerService } from 'ngx-matomo-client';
import { lastValueFrom, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import * as Sentry from '@sentry/angular-ivy';
import { ConfigService, Config } from '@app/config/service/config.service';
import { Configuration as ChatbotConfiguration } from '@app/openapi/chatbot-assistant-openapi';
import { PortalMessageBroker } from '@app/portal/portal-message-broker';
import { PortalTokenService } from '@app/portal/portal-token.service';
import { ContextInfoService, WellKnownJustFarmingProperties } from '@app/portal/context-info.service';
import { SentryUserContextService } from '@app/services/sentry-user-context.service';
import { environment } from '@environments/environment';

/**
 * Initialisierungs-Phase für Logging und Debugging
 */
type InitPhase = 'CONFIG' | 'LOGGING' | 'MONITORING' | 'INTEGRATION' | 'AUTH' | 'COMPLETE';

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  private readonly _configService = inject(ConfigService);
  private readonly _logger = inject(NGXLogger);
  private readonly _oidcSecurityService = inject(OidcSecurityService);
  private readonly _matomoInitializerService = inject(MatomoInitializerService);
  private readonly _chatbotConfiguration = inject(ChatbotConfiguration);
  private readonly _portalMessageBroker = inject(PortalMessageBroker);
  private readonly _portalTokenService = inject(PortalTokenService);
  private readonly _contextInfoService = inject(ContextInfoService);
  private readonly _sentryUserContext = inject(SentryUserContextService);

  private _currentPhase: InitPhase = 'CONFIG';

  /**
   * Führt die vollständige Initialisierung in definierter Reihenfolge durch.
   *
   * @returns Promise das resolved wenn alle Phasen abgeschlossen sind
   */
  async initialize(): Promise<void> {
    try {
      // Phase 1: Config laden (bereits durch ConfigModule APP_INITIALIZER erledigt)
      this.setPhase('CONFIG');
      const config = await this.waitForConfig();

      // Phase 2: Logging konfigurieren
      this.setPhase('LOGGING');
      this.initializeLogging(config);

      // Phase 3: Monitoring initialisieren (Sentry, Matomo)
      this.setPhase('MONITORING');
      this.initializeSentry(config);
      this.initializeMatomo(config);

      // Phase 4: Integrationen initialisieren (Portal, API)
      this.setPhase('INTEGRATION');
      await this.initializeIntegrations(config);

      // Phase 5: Auth-Status prüfen
      this.setPhase('AUTH');
      await this.initializeAuth();

      // Initialisierung abgeschlossen
      this.setPhase('COMPLETE');
      this._logger.info('Application initialization complete');
    } catch (error) {
      this._logger.error('Application initialization failed', {
        phase: this._currentPhase,
        error,
      });
      // Lasse Fehler durchreichen für Angular ErrorHandler
      throw error;
    }
  }

  /**
   * Setzt die aktuelle Phase und loggt den Übergang
   */
  private setPhase(phase: InitPhase): void {
    this._currentPhase = phase;
    // Debug-Level Logging für Phase-Tracking
    if (phase !== 'CONFIG') {
      this._logger.debug(`[AppInitializer] Phase: ${phase}`);
    }
  }

  /**
   * Wartet auf die geladene Konfiguration
   */
  private async waitForConfig(): Promise<Config> {
    return lastValueFrom(this._configService.loaded$.pipe(take(1)));
  }

  /**
   * Konfiguriert den Logger mit dem Level aus der Config
   */
  private initializeLogging(config: Config): void {
    this._logger.updateConfig({
      ...this._logger.getConfigSnapshot(),
      level: config.logging.level,
    });
  }

  /**
   * Initialisiert Sentry Error Reporting
   */
  private initializeSentry(config: Config): void {
    const sentry = config.sentry;
    const info = environment.info;

    if (!sentry) {
      return;
    }

    Sentry.init({
      enabled: sentry.enabled,
      debug: sentry.debug,
      environment: config.environment,
      release: info.commit,
      dsn: sentry.dsn,
      sampleRate: sentry.sampleRate,
      autoSessionTracking: false,
      enableTracing: false,
      maxBreadcrumbs: 0,
      initialScope: {
        extra: {
          info: info,
        },
      },
      beforeSend: (event) => {
        const contextInfo = this._contextInfoService?.contextInfo;
        if (contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)) {
          return event;
        }
        this._logger.debug('Error reporting is disabled, dropping event');
        return null;
      },
      beforeSendTransaction: (event) => {
        const contextInfo = this._contextInfoService?.contextInfo;
        if (
          contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS) &&
          contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
        ) {
          return event;
        }
        this._logger.debug('Error reporting or analytics are disabled, dropping transaction event');
        return null;
      },
      beforeBreadcrumb: (breadcrumb) => {
        const contextInfo = this._contextInfoService?.contextInfo;
        if (
          contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS) &&
          contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
        ) {
          return breadcrumb;
        }
        this._logger.debug('Error reporting or analytics are disabled, dropping breadcrumb');
        return null;
      },
    });
  }

  /**
   * Initialisiert Matomo Analytics
   */
  private initializeMatomo(config: Config): void {
    const matomoConfig = config.matomo;

    if (matomoConfig?.enabled && matomoConfig?.trackerUrl && matomoConfig?.siteId) {
      this._matomoInitializerService.initializeTracker({
        trackerUrl: matomoConfig.trackerUrl,
        siteId: matomoConfig.siteId,
      });
    }
  }

  /**
   * Initialisiert Portal-Integration und API-Konfiguration
   */
  private async initializeIntegrations(config: Config): Promise<void> {
    // API Base URL setzen
    this._chatbotConfiguration.basePath = config.chatbot.apiBaseUrl;

    // Portal initialisieren
    await this._portalMessageBroker.init(config.portal.targetOrigin);
    await this._portalTokenService.init(config.auth.renewTimeBeforeTokenExpiresInSeconds);

    // Sentry-Tags und User Context setzen
    const runningEmbedded = this._portalMessageBroker.isRunningInPortal();
    Sentry.setTag('runningEmbedded', runningEmbedded);

    if (runningEmbedded) {
      // Set Sentry user context from Portal token (embedded mode)
      const portalToken = this._portalTokenService.getAdnovaToken();
      this._sentryUserContext.setUserContextFromToken(portalToken, { runningEmbedded: true });
    } else {
      // Set Sentry user context from Keycloak token (standalone mode)
      // Handle case where no token is available yet (user not authenticated)
      const accessToken = await lastValueFrom(
        this._oidcSecurityService.getAccessToken().pipe(
          take(1), // Take first value (can be null if not authenticated)
          catchError((err) => {
            // Fehler nicht verschleiern (ALLG-2): loggen, Init läuft ohne
            // Sentry-User-Context weiter (nicht kritisch für den App-Start).
            this._logger.warn('Failed to get access token for Sentry user context', { error: err });
            return of(null);
          }),
        ),
      );

      // Only set user context if we have a valid token
      if (accessToken) {
        this._sentryUserContext.setUserContextFromToken(accessToken);
      }
    }
  }

  /**
   * Prüft den OAuth-Authentifizierungsstatus
   */
  private async initializeAuth(): Promise<void> {
    try {
      const result = await lastValueFrom(this._oidcSecurityService.checkAuth().pipe(take(1)));

      if (result.isAuthenticated) {
        this._logger.info('APP_INITIALIZER: User authenticated');
      } else {
        this._logger.info('APP_INITIALIZER: User not authenticated - AuthGuard will redirect to Keycloak');
      }
    } catch (error) {
      this._logger.error('APP_INITIALIZER: OAuth authentication failed', {
        error: error instanceof Error ? error.message : error,
        hint: 'Check Keycloak client configuration (redirectUrl, clientId, scope)',
      });
      // Allow app to continue even on auth failure
    }
  }
}
